import { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';

/**
 * useConsentStatus — Check user's consent status for a content key.
 *
 * Calls GET /api/user/consents/{key}.
 * Returns { loading, error, status, acceptedVersion, currentVersion, requiresAction, refetch }.
 *
 * status: "NEVER_ACCEPTED" | "CURRENT" | "VERSION_OUTDATED" | "WITHDRAWN" | null
 * If user is not authenticated, returns null status (non-blocking).
 */
export function useConsentStatus(contentKey) {
  const [state, setState] = useState({
    loading: true,
    error: null,
    status: null,
    acceptedVersion: null,
    currentVersion: null,
    requiresAction: false,
  });

  const fetchStatus = useCallback(async () => {
    if (!contentKey) {
      setState({ loading: false, error: null, status: null, acceptedVersion: null, currentVersion: null, requiresAction: false });
      return;
    }

    // NOTE (QA fix, iteration_360): the previous guard referenced an
    // undefined `token` variable (leftover from the pre-cookie-auth
    // transport) and threw `ReferenceError: token is not defined` on
    // every mount, surfacing a full-screen CRA error overlay on every
    // page that uses ConsentGate/useConsentStatus (e.g. patient intake).
    // Auth is cookie-based now; the 401 branch below already handles
    // the unauthenticated case non-blockingly.

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const { data } = await api.get(`/user/consents/${contentKey}`);

      setState({
        loading: false,
        error: null,
        status: data.status,
        acceptedVersion: data.accepted_version,
        currentVersion: data.current_version,
        requiresAction: data.requires_action,
      });
    } catch (err) {
      if (err.response?.status === 401) {
        // Not authenticated — don't treat as error
        setState({ loading: false, error: null, status: null, acceptedVersion: null, currentVersion: null, requiresAction: false });
      } else {
        setState({
          loading: false,
          error: err.response?.data?.detail || err.message,
          status: null,
          acceptedVersion: null,
          currentVersion: null,
          requiresAction: false,
        });
      }
    }
  }, [contentKey]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return { ...state, refetch: fetchStatus };
}
